// ActiveDirectoryMain.tsx
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMemo, useEffect, useRef } from 'react';
import { ActiveDirectorySidebar, AD_TABS } from './ActiveDirectorySidebar';
import Domain from './Domain';
import OUs from './OUs';
import ADUsers from './ADUsers';
import ADGroups from './ADGroups';
import ADComputers from './ADComputers';
import ADGPOs from './ADGPOs';
import ADPasswordPolicies from './ADPasswordPolicies';
import { ArrowBigLeft, Server, ScanSearch, Network } from 'lucide-react';
import baseapi from '../../services/baseapi';
import { showSuccess, showError } from '../../utils/toast';
import { useClientLevel } from '../../hooks/useClientLevel';

// ── Placeholder tab content components ──────────────────────────────────────

// ────────────────────────────────────────────────────────────────────────────

export default function ActiveDirectoryMain() {

    const [searchParams, setSearchParams] = useSearchParams();
    const info = JSON.parse(sessionStorage.getItem("internal_ids") || "{}");
    const asset_id = info.asset_id;
    const name = info.name;
    const agent_id = info.agent_id;

    const handleInitiateADScan = async () => {
        try {
            const response = await baseapi.post('/agents/agent-task', {
                agent_id: agent_id,
                job_type: 'ad_scan'
            });
            if (response.data.status) {
                showSuccess(response.data.message || "AD Scan initiated successfully");
            } else {
                showError(response.data.message || "Failed to initiate AD Scan");
            }
        } catch (error: any) {
            showError(error.message);
        }
    };

    const requestedTab = searchParams.get('tab');
    const navigate = useNavigate();
    const activeTab = useMemo(() => {
        const isValid = AD_TABS.some(t => t.id === requestedTab);
        return isValid && requestedTab ? requestedTab : AD_TABS[0].id;
    }, [requestedTab]);

    const TAB_COMPONENTS: Record<string, React.ReactNode> = {
        "domain": <Domain asset_id={asset_id} />,
        "ous": <OUs asset_id={asset_id} />,
        "users": <ADUsers asset_id={asset_id} />,
        "groups": <ADGroups asset_id={asset_id} />,
        "computers": <ADComputers asset_id={asset_id} />,
        "gpos": <ADGPOs asset_id={asset_id} />,
        "password-policies": <ADPasswordPolicies asset_id={asset_id} />,
    };

    const level = useClientLevel();
    const isFirstRender = useRef(true);

    useEffect(() => {
        // Skip navigation on the initial mount/refresh
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        // Navigate only when level actually changes after initial load
        navigate("/asset");

    }, [level, navigate]);

    useEffect(() => {
        if (requestedTab !== activeTab) {
            setSearchParams({ tab: activeTab }, { replace: true });
        }
    }, [activeTab, requestedTab, setSearchParams]);

    return (
        <div className="flex w-full bg-gray-900 text-gray-200 h-[calc(100vh-4rem)] overflow-hidden">
            {/* Sidebar */}
            <ActiveDirectorySidebar
                tabs={AD_TABS}
                activeTab={activeTab}
                onTabChange={(id) => setSearchParams({ tab: id })}
            />

            {/* Main Content */}
            <div className="flex-1 min-w-0 bg-gray-900 overflow-y-auto custom-scroll">

                <div className='flex justify-between border-b border-gray-600 bg-gray-900 p-2 sticky top-0 z-20'>
                    <div className='flex items-center gap-2'>
                        <div className='bg-orange-600/20 p-1.5 rounded-lg border border-orange-500/30 shrink-0'>
                            <Server className='text-orange-600 w-4 h-4' />
                        </div>
                        <h1 className='text-sm font-bold text-white tracking-wide'>{name}</h1>
                    </div>

                    <div className='flex items-center gap-2'>
                        <button
                            className='bg-orange-600/20 hover:bg-orange-600/30 border border-orange-500/40 text-orange-400 flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-semibold'
                            onClick={() => navigate('/asset/active-directory-v2')}
                            title="Open Active Directory Topology v2 (React Flow)"
                        >
                            <Network className="w-4 h-4 text-orange-400" />
                            <span>AD Topology v2</span>
                        </button>
                        <button
                            className='bg-orange-600 hover:bg-orange-500 flex items-center gap-1 text-white px-4 py-2 rounded-lg'
                            onClick={handleInitiateADScan}
                        >
                            <ScanSearch className="w-4 h-4" />
                            <span className="text-xs font-semibold">Initiate AD Scan</span>
                        </button>
                        <button
                            className='bg-orange-700 hover:bg-orange-600 flex items-center gap-1 text-white px-4 py-2 rounded-lg'
                            onClick={() => navigate('/asset/dashboard')}
                        >
                            <ArrowBigLeft className="w-4 h-4" />
                            <span className="text-xs font-semibold">Back</span>
                        </button>
                    </div>
                </div>

                <div className="mx-auto w-full pb-12 p-4">
                    {TAB_COMPONENTS[activeTab]}
                </div>

            </div>
        </div>
    );
}